import { useState, useEffect, useCallback } from 'react';

// Utility per verificare se siamo in ambiente browser
const isBrowser = typeof window !== 'undefined';

interface GeolocationState {
  position: GeolocationPosition | null;
  error: GeolocationPositionError | null;
  loading: boolean;
}

interface UseGeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
  watch?: boolean;
  showSuccessToast?: boolean; // Compatibilità con API esistente
}

interface Coordinates {
  latitude: number;
  longitude: number;
}

interface UseGeolocationReturn extends GeolocationState {
  getCurrentPosition: () => void;
  requestPermission: () => Promise<PermissionState | 'error' | 'unsupported'>;
  coordinates: Coordinates | null; // Compatibilità con API esistente
}

export function useGeolocation(options: UseGeolocationOptions = {}): UseGeolocationReturn {
  const [state, setState] = useState<GeolocationState>({
    position: null,
    error: null,
    loading: false,
  });

  const {
    enableHighAccuracy = true,
    timeout = 10000,
    maximumAge = 300000, // 5 minutes
    watch = false,
    showSuccessToast = true, // Default true per compatibilità
  } = options;

  // Auto-start geolocation on mount
  useEffect(() => {
    // Skip geolocation in SSR environment
    if (!isBrowser) {
      console.log('🎯 Skipping geolocation in SSR environment');
      return;
    }

    if (!navigator.geolocation) {
      setState(prev => ({
        ...prev,
        error: {
          code: 2,
          message: 'Geolocation is not supported by this browser.',
        } as GeolocationPositionError,
        loading: false,
      }));
      return;
    }

    console.log('🎯 Auto-starting geolocation...');
    setState(prev => ({ ...prev, loading: true }));

    const positionOptions: PositionOptions = {
      enableHighAccuracy,
      timeout,
      maximumAge,
    };

    const onSuccess = (position: GeolocationPosition) => {
      console.log('🎯 Geolocation success:', position.coords.latitude, position.coords.longitude);
      setState({
        position,
        error: null,
        loading: false,
      });
    };

    const onError = (error: GeolocationPositionError) => {
      console.error('❌ Geolocation error:', error);
      setState(prev => ({
        ...prev,
        error,
        loading: false,
      }));
    };

    let watchId: number | undefined;

    if (watch) {
      watchId = navigator.geolocation.watchPosition(
        onSuccess,
        onError,
        positionOptions
      );
    } else {
      navigator.geolocation.getCurrentPosition(
        onSuccess,
        onError,
        positionOptions
      );
    }

    return () => {
      if (watchId !== undefined) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [enableHighAccuracy, timeout, maximumAge, watch]);

  const requestPermission = useCallback(async () => {
    if (!isBrowser || !navigator.permissions) {
      return 'unsupported';
    }

    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      return permission.state;
    } catch (error) {
      console.error('Error checking geolocation permission:', error);
      return 'error';
    }
  }, []);

  const getCurrentPosition = useCallback(() => {
    if (!isBrowser || !navigator.geolocation) {
      setState(prev => ({
        ...prev,
        error: {
          code: 2,
          message: 'Geolocation is not supported by this browser.',
        } as GeolocationPositionError,
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('🎯 Manual geolocation success:', position.coords.latitude, position.coords.longitude);
        setState({
          position,
          error: null,
          loading: false,
        });
      },
      (error) => {
        console.error('❌ Manual geolocation error:', error);
        setState(prev => ({
          ...prev,
          error,
          loading: false,
        }));
      },
      {
        enableHighAccuracy,
        timeout,
        maximumAge,
      }
    );
  }, [enableHighAccuracy, timeout, maximumAge]);

  // Calcola coordinates per compatibilità con API esistente
  const coordinates: Coordinates | null = state.position ? {
    latitude: state.position.coords.latitude,
    longitude: state.position.coords.longitude,
  } : null;

  return {
    ...state,
    requestPermission,
    getCurrentPosition,
    coordinates, // Aggiunto per compatibilità
  };
}
